import React from "react";
import {
  ArrowRight,
  ChevronRight,
  Bot,
  Activity,
  Sparkles,
  Phone,
  Calendar,
  TrendingUp,
} from "lucide-react";

const TrustBadge = () => (
  <div
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs text-zinc-300"
    data-testid="hero-trust-badge"
  >
    <span className="live-dot" />
    <span className="font-mono">SYSTEMS LIVE</span>
    <span className="text-zinc-500">·</span>
    <span>Built for 2026</span>
  </div>
);

const MetricChip = ({ label, value, delay = 0 }) => (
  <div
    className="glass rounded-xl px-4 py-3 min-w-[120px]"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
      {label}
    </div>
    <div className="text-lg font-semibold text-white mt-1 font-mono">
      {value}
    </div>
  </div>
);

const DashboardMockup = () => {
  const bars = [40, 65, 55, 80, 72, 95, 60, 85, 70];
  return (
    <div className="hero-tilt" data-testid="hero-dashboard">
      <div className="hero-tilt-inner relative">
        {/* Glow */}
        <div className="absolute -inset-12 bg-[#39FF14]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute -inset-12 -translate-x-20 bg-[#007AFF]/15 blur-[140px] rounded-full pointer-events-none" />

        {/* Main panel */}
        <div className="relative glass-strong rounded-3xl p-5 lg:p-6 w-full max-w-[560px]">
          {/* top bar */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-zinc-700" />
              <div className="w-2 h-2 rounded-full bg-zinc-700" />
              <div className="w-2 h-2 rounded-full bg-[#39FF14]" />
            </div>
            <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
              corescale / overview
            </div>
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-[#39FF14]">
              <span className="live-dot" />
              LIVE
            </div>
          </div>

          {/* KPI Row */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: "Leads", value: "1,248", trend: "+24%" },
              { label: "Booked", value: "312", trend: "+18%" },
              { label: "Calls", value: "97%", trend: "+6.2%" },
            ].map((k, i) => (
              <div key={k.label} className="glass rounded-xl p-3">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">
                  {k.label}
                </div>
                <div className="flex items-baseline justify-between mt-1">
                  <div className="text-lg font-semibold text-white font-mono">
                    {k.value}
                  </div>
                  <div className="text-[10px] text-[#39FF14] font-mono">
                    {k.trend}
                  </div>
                </div>
                <div className="mt-2 h-1 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#39FF14] to-[#007AFF]"
                    style={{ width: `${60 + i * 12}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="glass rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#39FF14]" />
                <span className="text-xs text-zinc-300">
                  Automation throughput
                </span>
              </div>
              <span className="text-[10px] font-mono text-zinc-500">
                30D
              </span>
            </div>
            <div className="flex items-end gap-1.5 h-20">
              {bars.map((h, i) => (
                <div
                  key={i}
                  className="mini-bar flex-1 rounded-sm"
                  style={{
                    height: `${h}%`,
                    background:
                      i === bars.length - 1
                        ? "linear-gradient(180deg, #39FF14, #007AFF)"
                        : "rgba(255,255,255,0.12)",
                    animationDelay: `${i * 0.05}s`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Live activity */}
          <div className="space-y-2">
            {[
              {
                icon: Phone,
                text: "AI Receptionist answered call",
                meta: "00:12 · qualified lead",
              },
              {
                icon: Calendar,
                text: "Appointment booked",
                meta: "Tue · 3:30 PM",
              },
              {
                icon: Bot,
                text: "CRM synced · workflow complete",
                meta: "0.4s · auto",
              },
            ].map((a, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5"
              >
                <div className="w-8 h-8 rounded-lg bg-[#39FF14]/10 border border-[#39FF14]/20 flex items-center justify-center text-[#39FF14]">
                  <a.icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white truncate">{a.text}</div>
                  <div className="text-[10px] font-mono text-zinc-500">
                    {a.meta}
                  </div>
                </div>
                <div className="text-[10px] font-mono text-[#39FF14]">
                  ✓
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating chips */}
        <div className="hidden md:flex absolute -left-12 top-16 animate-float-slow">
          <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#39FF14] to-[#007AFF] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                AI Action
              </div>
              <div className="text-xs text-white font-medium">
                Lead qualified · sent to CRM
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:flex absolute -right-10 bottom-10 animate-float-medium">
          <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center">
              <Activity className="w-4 h-4 text-[#39FF14]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Uptime
              </div>
              <div className="text-xs text-white font-mono">99.98%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-28 sm:pt-32 lg:pt-40 pb-16 sm:pb-20 lg:pb-28 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background grid + glows */}
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full bg-[#39FF14]/10 blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[#007AFF]/15 blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="animate-fade-up">
            <TrustBadge />
          </div>
          <h1
            className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.05] animate-fade-up"
            style={{ animationDelay: "0.1s" }}
            data-testid="hero-headline"
          >
            Scale Smarter <br />
            With <span className="gradient-text-green">AI & Automation</span>
          </h1>
          <p
            className="mt-7 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed animate-fade-up"
            style={{ animationDelay: "0.2s" }}
            data-testid="hero-subheadline"
          >
            Stop missing calls. Capture every after-hours lead, automate
            follow-ups, and turn inbound into booked appointments — on autopilot.
            Built for service businesses ready to scale without scaling headcount.
          </p>

          <div
            className="mt-6 flex flex-wrap items-center gap-2 animate-fade-up"
            style={{ animationDelay: "0.25s" }}
            data-testid="hero-trust-row"
          >
            {[
              "24/7 lead capture",
              "Human-sounding AI",
              "CRM-integrated",
              "Production-grade",
            ].map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-zinc-300 border border-white/8 bg-white/[0.02]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14]" />
                {t}
              </span>
            ))}
          </div>

          <div
            className="mt-8 flex flex-wrap items-center gap-4 animate-fade-up"
            style={{ animationDelay: "0.3s" }}
          >
            <a
              href="https://cal.com/erik-carlson/secret"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#39FF14] text-black font-semibold hover:shadow-[0_0_40px_rgba(57,255,20,0.5)] transition-all hover:bg-white"
              data-testid="hero-cta-primary"
            >
              Book A Strategy Call
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href="#services"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 hover:border-white/30 transition-all"
              data-testid="hero-cta-secondary"
            >
              Explore Solutions
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>

          <div
            className="mt-12 flex flex-wrap items-center gap-3 animate-fade-up"
            style={{ animationDelay: "0.45s" }}
          >
            <MetricChip label="24/7" value="AI Receptionist" />
            <MetricChip label="< 0.4s" value="Automations" />
            <MetricChip label="∞" value="Lead Capture" />
          </div>
        </div>

        <div className="lg:col-span-5">
          <DashboardMockup />
        </div>
      </div>

      {/* Marquee logos / capabilities */}
      <div className="relative mt-20 border-y border-white/5 py-5 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none" />
        <div className="marquee flex gap-12 whitespace-nowrap text-zinc-500 text-sm">
          {[
            "AI Receptionists",
            "Workflow Automation",
            "Lead Funnels",
            "CRM Integrations",
            "Mobile Apps",
            "Custom AI",
            "Booking Systems",
            "Analytics Dashboards",
            "Call Routing",
            "SMS Automation",
          ]
            .concat([
              "AI Receptionists",
              "Workflow Automation",
              "Lead Funnels",
              "CRM Integrations",
              "Mobile Apps",
              "Custom AI",
              "Booking Systems",
              "Analytics Dashboards",
              "Call Routing",
              "SMS Automation",
            ])
            .map((t, i) => (
              <span key={i} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14]/60" />
                <span className="uppercase tracking-[0.25em] text-xs">
                  {t}
                </span>
              </span>
            ))}
        </div>
      </div>
    </section>
  );
}
